import * as React from 'react';
import { useState, useEffect } from 'react';

interface Agent {
  id: string;
  name: string;
  description?: string;
}

export interface QuickAction {
  id: string;
  icon: string;
  label: string;
  prompt: string;
}

interface AgentSettingsProps {
  apiUrl: string;
  token: string;
  currentAgentId: string;
  userIdentity: string;
  hostcli?: string;
  quickActions?: QuickAction[];
  showDebug: boolean;
  documentName?: string;
  onAgentChange: (agentId: string) => void;
  onIdentityChange: (identity: string) => void;
  onQuickActionsChange?: (actions: QuickAction[]) => void;
  onDebugToggle: (show: boolean) => void;
  onDocumentNameChange?: (name: string) => void;
  onClose: () => void;
}

export const AgentSettings: React.FC<AgentSettingsProps> = ({
  apiUrl,
  token,
  currentAgentId,
  userIdentity,
  hostcli,
  quickActions = [],
  showDebug,
  documentName = '',
  onAgentChange,
  onIdentityChange,
  onQuickActionsChange,
  onDebugToggle,
  onDocumentNameChange,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<'agent' | 'identity' | 'quickActions' | 'preferences' | 'debug'>('agent');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [identityText, setIdentityText] = useState(userIdentity);
  const [editingActions, setEditingActions] = useState<QuickAction[]>(quickActions);
  const [editingActionId, setEditingActionId] = useState<string | null>(null);
  const [newAction, setNewAction] = useState<Partial<QuickAction>>({});
  const [docNameText, setDocNameText] = useState(documentName);
  const take = 20;

  const fetchAgents = async (reset: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      const skip = reset ? 0 : agents.length;

      const query = `
        query getAllMyAgents($take: Int, $skip: Int, $search: String, $orderBy: IAsOrderByInput) {
          getAllMyIAs(take: $take, skip: $skip, search: $search, orderBy: $orderBy) {
            totalCount
            edges {
              id
              name
              description
            }
          }
        }
      `;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      if (hostcli && hostcli.trim().length > 0) {
        headers['hostcli'] = hostcli;
      }

      const response = await fetch(`${apiUrl}/graphql`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query,
          variables: { take, skip }
        })
      });

      const data = await response.json();

      if (!response.ok) {
        let errorDetail = `Status: ${response.status} ${response.statusText}\n`;
        errorDetail += `API: ${apiUrl}\n`;
        errorDetail += `Headers: ${JSON.stringify(headers, null, 2)}\n`;
        if (data.errors) {
          errorDetail += `GraphQL Errors: ${JSON.stringify(data.errors, null, 2)}`;
        } else if (data.message) {
          errorDetail += `Message: ${data.message}`;
        } else {
          errorDetail += `Response: ${JSON.stringify(data, null, 2)}`;
        }
        throw new Error(errorDetail);
      }

      if (data.errors) {
        const errorDetail = `GraphQL Errors:\n${JSON.stringify(data.errors, null, 2)}`;
        throw new Error(errorDetail);
      }

      const result = data.data.getAllMyIAs;
      const fetchedAgents = result.edges || [];
      const total = result.totalCount || 0;

      // Ajouter l'agent par défaut au début
      const defaultAgent: Agent = {
        id: 'devana',
        name: 'Agent par défaut',
        description: 'Agent Devana par défaut'
      };

      if (reset) {
        setAgents([defaultAgent, ...fetchedAgents]);
      } else {
        setAgents(prev => [...prev, ...fetchedAgents]);
      }

      setTotalCount(total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents(true);
  }, []);

  const handleLoadMore = () => {
    fetchAgents(false);
  };

  const handleAgentSelect = (agentId: string) => {
    onAgentChange(agentId);
  };

  const handleIdentitySave = () => {
    onIdentityChange(identityText);
    onClose();
  };

  const handleAddAction = () => {
    if (!newAction.icon || !newAction.label || !newAction.prompt) return;

    const action: QuickAction = {
      id: Date.now().toString(),
      icon: newAction.icon,
      label: newAction.label,
      prompt: newAction.prompt
    };

    const updated = [...editingActions, action];
    setEditingActions(updated);
    setNewAction({});
  };

  const handleDeleteAction = (id: string) => {
    const updated = editingActions.filter(a => a.id !== id);
    setEditingActions(updated);
  };

  const handleSaveActions = () => {
    if (onQuickActionsChange) {
      onQuickActionsChange(editingActions);
      onClose();
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#333' }}>Paramètres</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e0e0e0',
          padding: '0 20px',
          overflowX: 'auto',
          scrollbarWidth: 'thin'
        }}>
          <button
            onClick={() => setActiveTab('agent')}
            style={{
              minWidth: '100px',
              padding: '12px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'agent' ? '2px solid #4CAF50' : '2px solid transparent',
              color: activeTab === 'agent' ? '#4CAF50' : '#666',
              fontWeight: activeTab === 'agent' ? 600 : 400,
              transition: 'all 0.2s',
              fontSize: '13px',
              whiteSpace: 'nowrap'
            }}
          >
            Agent
          </button>
          <button
            onClick={() => setActiveTab('identity')}
            style={{
              minWidth: '110px',
              padding: '12px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'identity' ? '2px solid #4CAF50' : '2px solid transparent',
              color: activeTab === 'identity' ? '#4CAF50' : '#666',
              fontWeight: activeTab === 'identity' ? 600 : 400,
              transition: 'all 0.2s',
              fontSize: '13px',
              whiteSpace: 'nowrap'
            }}
          >
            Mon identité
          </button>
          <button
            onClick={() => setActiveTab('quickActions')}
            style={{
              minWidth: '130px',
              padding: '12px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'quickActions' ? '2px solid #4CAF50' : '2px solid transparent',
              color: activeTab === 'quickActions' ? '#4CAF50' : '#666',
              fontWeight: activeTab === 'quickActions' ? 600 : 400,
              transition: 'all 0.2s',
              fontSize: '13px',
              whiteSpace: 'nowrap'
            }}
          >
            Actions rapides
          </button>
          <button
            onClick={() => setActiveTab('preferences')}
            style={{
              minWidth: '110px',
              padding: '12px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'preferences' ? '2px solid #4CAF50' : '2px solid transparent',
              color: activeTab === 'preferences' ? '#4CAF50' : '#666',
              fontWeight: activeTab === 'preferences' ? 600 : 400,
              transition: 'all 0.2s',
              fontSize: '13px',
              whiteSpace: 'nowrap'
            }}
          >
            Préférences
          </button>
          <button
            onClick={() => setActiveTab('debug')}
            style={{
              minWidth: '90px',
              padding: '12px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'debug' ? '2px solid #4CAF50' : '2px solid transparent',
              color: activeTab === 'debug' ? '#4CAF50' : '#666',
              fontWeight: activeTab === 'debug' ? 600 : 400,
              transition: 'all 0.2s',
              fontSize: '13px',
              whiteSpace: 'nowrap'
            }}
          >
            Debug
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px'
        }}>
          {activeTab === 'agent' ? (
            <div>
              {loading && agents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  Chargement des agents...
                </div>
              ) : error ? (
                <div style={{
                  padding: '20px',
                  backgroundColor: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: '8px',
                  color: '#c33'
                }}>
                  {error}
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '10px', color: '#666', fontSize: '14px' }}>
                    Sélectionnez un agent pour vos conversations :
                  </div>
                  {agents.map(agent => (
                    <div
                      key={agent.id}
                      onClick={() => handleAgentSelect(agent.id)}
                      style={{
                        padding: '16px',
                        marginBottom: '10px',
                        border: agent.id === currentAgentId ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: agent.id === currentAgentId ? '#f0f8f0' : 'white',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (agent.id !== currentAgentId) {
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (agent.id !== currentAgentId) {
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      <div style={{
                        fontWeight: 600,
                        marginBottom: agent.description ? '4px' : '0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#333'
                      }}>
                        {agent.id === currentAgentId && <span style={{ color: '#4CAF50' }}>✓</span>}
                        {agent.name}
                      </div>
                      {agent.description && (
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {agent.description}
                        </div>
                      )}
                    </div>
                  ))}

                  {agents.length < totalCount && (
                    <button
                      onClick={handleLoadMore}
                      disabled={loading}
                      style={{
                        width: '100%',
                        padding: '12px',
                        marginTop: '10px',
                        border: '1px solid #4CAF50',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        color: '#4CAF50',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontWeight: 500,
                        opacity: loading ? 0.6 : 1
                      }}
                    >
                      {loading ? 'Chargement...' : 'Charger plus'}
                    </button>
                  )}
                </>
              )}
            </div>
          ) : activeTab === 'identity' ? (
            <div>
              <div style={{ marginBottom: '12px', fontSize: '14px', color: '#666' }}>
                Définissez votre identité pour personnaliser les réponses de l'IA :
              </div>
              <textarea
                value={identityText}
                onChange={(e) => setIdentityText(e.target.value)}
                placeholder="Ex: Je suis développeur React avec 5 ans d'expérience..."
                style={{
                  width: '100%',
                  minHeight: '200px',
                  padding: '12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
              <button
                onClick={handleIdentitySave}
                style={{
                  marginTop: '12px',
                  padding: '12px 24px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  width: '100%'
                }}
              >
                Enregistrer l'identité
              </button>
            </div>
          ) : activeTab === 'quickActions' ? (
            <div>
              <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                Personnalisez les actions rapides qui apparaissent quand du texte est sélectionné.
              </div>

              {/* Note d'information */}
              <div style={{
                padding: '12px',
                backgroundColor: '#e3f2fd',
                border: '1px solid #90caf9',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '13px',
                color: '#1976d2'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>ℹ️ Comment ça marche ?</div>
                Les actions rapides permettent d'appliquer des transformations au texte sélectionné.
                Les balises <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 4px', borderRadius: '3px' }}>[RESULT]...[/RESULT]</code> permettent
                au bouton "Remplacer" d'identifier exactement le contenu à insérer.
              </div>

              {/* Liste des actions existantes */}
              <div style={{ marginBottom: '20px' }}>
                {editingActions.map(action => (
                  <div
                    key={action.id}
                    style={{
                      padding: '12px',
                      marginBottom: '8px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: 'white'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <span style={{ fontSize: '20px' }}>{action.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>
                          {action.label}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                          {action.prompt}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteAction(action.id)}
                      style={{
                        background: '#fee',
                        border: '1px solid #fcc',
                        borderRadius: '6px',
                        color: '#c33',
                        padding: '6px 12px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      Supprimer
                    </button>
                  </div>
                ))}

                {editingActions.length === 0 && (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#999',
                    fontSize: '14px'
                  }}>
                    Aucune action rapide personnalisée.
                    Ajoutez-en une ci-dessous !
                  </div>
                )}
              </div>

              {/* Formulaire d'ajout */}
              <div style={{
                padding: '16px',
                backgroundColor: '#f8f8f8',
                borderRadius: '8px',
                marginBottom: '12px'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '12px', fontSize: '14px', color: '#333' }}>
                  Ajouter une nouvelle action
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#666' }}>
                    Icône (emoji)
                  </label>
                  <input
                    type="text"
                    value={newAction.icon || ''}
                    onChange={(e) => setNewAction({ ...newAction, icon: e.target.value })}
                    placeholder="Ex: ✨"
                    maxLength={2}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#666' }}>
                    Label
                  </label>
                  <input
                    type="text"
                    value={newAction.label || ''}
                    onChange={(e) => setNewAction({ ...newAction, label: e.target.value })}
                    placeholder="Ex: Reformuler"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#666' }}>
                    Prompt
                  </label>
                  <textarea
                    value={newAction.prompt || ''}
                    onChange={(e) => setNewAction({ ...newAction, prompt: e.target.value })}
                    placeholder="Ex: Reformule ce texte pour le rendre plus professionnel:"
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                  <div style={{
                    fontSize: '11px',
                    color: '#666',
                    marginTop: '4px',
                    lineHeight: '1.4'
                  }}>
                    💡 <strong>Astuce:</strong> Le système demande automatiquement au LLM d'utiliser les balises [RESULT] pour que le bouton "Remplacer" fonctionne correctement.
                  </div>
                </div>

                <button
                  onClick={handleAddAction}
                  disabled={!newAction.icon || !newAction.label || !newAction.prompt}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: (!newAction.icon || !newAction.label || !newAction.prompt) ? '#ddd' : '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (!newAction.icon || !newAction.label || !newAction.prompt) ? 'not-allowed' : 'pointer',
                    fontWeight: 500,
                    fontSize: '14px'
                  }}
                >
                  + Ajouter l'action
                </button>
              </div>

              <button
                onClick={handleSaveActions}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '14px'
                }}
              >
                Enregistrer les actions
              </button>
            </div>
          ) : activeTab === 'preferences' ? (
            <div>
              <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                Personnalisez les préférences de l'add-in.
              </div>

              <div style={{
                padding: '16px',
                backgroundColor: '#f8f8f8',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#333' }}>
                    Nom du document
                  </label>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    Personnalisez le nom du document pour le contexte IA
                  </div>
                  <input
                    type="text"
                    value={docNameText}
                    onChange={(e) => setDocNameText(e.target.value)}
                    placeholder="Ex: Rapport annuel 2024"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <button
                  onClick={() => {
                    if (onDocumentNameChange) {
                      onDocumentNameChange(docNameText);
                      onClose();
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 24px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '14px'
                  }}
                >
                  Enregistrer le nom
                </button>
              </div>

              <div style={{
                padding: '12px',
                backgroundColor: '#e3f2fd',
                border: '1px solid #90caf9',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#1976d2'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>ℹ️ À propos</div>
                Le nom du document est utilisé pour donner du contexte à l'IA lors des conversations.
              </div>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                Options de débogage et informations techniques.
              </div>

              <div style={{
                padding: '16px',
                backgroundColor: '#f8f8f8',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '8px'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                      Afficher les informations de debug
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Active l'affichage des logs console et des panels de diagnostic
                    </div>
                  </div>
                  <label style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '50px',
                    height: '26px',
                    flexShrink: 0
                  }}>
                    <input
                      type="checkbox"
                      checked={showDebug}
                      onChange={(e) => onDebugToggle(e.target.checked)}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: showDebug ? '#4CAF50' : '#ccc',
                      transition: '0.3s',
                      borderRadius: '26px'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '',
                        height: '18px',
                        width: '18px',
                        left: showDebug ? '28px' : '4px',
                        bottom: '4px',
                        backgroundColor: 'white',
                        transition: '0.3s',
                        borderRadius: '50%'
                      }} />
                    </span>
                  </label>
                </div>
              </div>

              <div style={{
                padding: '12px',
                backgroundColor: '#e3f2fd',
                border: '1px solid #90caf9',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#1976d2'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>ℹ️ À propos</div>
                Les informations de debug sont utiles pour diagnostiquer les problèmes de connexion
                et comprendre le fonctionnement de l'add-in.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
